import React, { useState, useRef, useEffect } from 'react';
import { MaintenanceImage } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Props for the PostImageToFeedModal component.
 */
interface PostImageToFeedModalProps {
  image: MaintenanceImage;
  onClose: () => void;
  onPost: (comment: string) => Promise<void>;
}

/**
 * A modal component for posting an existing image attachment to the activity feed
 * with an optional comment.
 * @param {PostImageToFeedModalProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered modal.
 */
const PostImageToFeedModal: React.FC<PostImageToFeedModalProps> = ({ image, onClose, onPost }) => {
  // State for modal visibility and animations.
  const [isShowing, setIsShowing] = useState(false);
  // State for the user's comment.
  const [comment, setComment] = useState('');
  // State to manage the loading status of the post action.
  const [isPosting, setIsPosting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Apply focus trap when the modal is visible.
  useFocusTrap(modalRef, isShowing);

  // Handle modal animation and Escape key listener on mount.
  useEffect(() => {
    setIsShowing(true);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  /**
   * Closes the modal with a fade-out animation. Prevents closing if a post is in progress.
   */
  const handleClose = () => {
    if (isPosting) return;
    setIsShowing(false);
    setTimeout(onClose, 300);
  };

  /**
   * Handles the form submission to post the image and comment.
   * @param {React.FormEvent} e - The form event.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPosting) return;
    setIsPosting(true);
    try {
      await onPost(comment);
      handleClose();
    } catch (error) {
      console.error('Failed to post image:', error);
      // The parent component is responsible for showing any user-facing alerts.
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black z-50 flex justify-center items-center transition-opacity duration-300 ${isShowing ? 'bg-opacity-60' : 'bg-opacity-0'}`}
      aria-labelledby="post-image-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-2xl w-full max-w-md m-4 transform transition-all duration-300 ease-out ${isShowing ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-gray-200">
            <h2 id="post-image-modal-title" className="text-xl font-bold text-gray-900">Post Image to Activity</h2>
          </div>
          <div className="p-6 space-y-4">
            <img src={image.image_url_s3} alt="Attachment to post" className="w-full h-auto max-h-60 object-contain rounded-md border" />
            <div>
              <label htmlFor="image-comment" className="block text-sm font-medium text-gray-700 mb-1">Add a comment (optional)</label>
              <textarea
                id="image-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., This is the part that's broken."
              />
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end items-center space-x-3 rounded-b-xl">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPosting}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed"
            >
              {isPosting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostImageToFeedModal;
